import dayjs from 'dayjs';

import calcularGrupo from 'utils/calcularGrupo';
import formatMoney from 'utils/formatMoney';
import Logo from 'utils/Logo';
import SorteioPremioBilhete from 'components/SorteioPremioBilhete';

import './styles.css';

/**
 * Devolve o número sorteado como string de dígitos, ou '' quando o valor não
 * presta (null, vazio, texto). O `1_ao_7`/`1_ao_10` é digitado no painel e já
 * chegou em produção com null no lugar de um prêmio.
 */
function normalizaResultado(resultado) {
  const numero = String(resultado ?? '').trim();
  return /^\d+$/.test(numero) ? numero : '';
}

/**
 * Dado um número de resultado (4 dígitos), retorna o grupo (1-25).
 * Mesma lógica do backend: Math.ceil(ultimos2 / 4), com 0 → 25.
 * Prêmio inválido devolve null — aquele sorteio não confere ninguém.
 */
function grupoDoResultado(resultado) {
  const numero = normalizaResultado(resultado);
  if (numero.length < 2) return null;

  const ultimos2 = parseInt(numero.slice(-2), 10);
  return ultimos2 === 0 ? 25 : Math.ceil(ultimos2 / 4);
}

/**
 * Números sorteados da extração, na ordem dos prêmios (índice 0 = 1º prêmio).
 *
 * Espelha o ResultadoNumerosResolver do backend: `resultado.resultados` guarda
 * só os 5 primeiros prêmios; do 6º em diante os números ficam em `1_ao_7` /
 * `1_ao_10`. Sem isso um palpite 1/10 conferia apenas contra os 5 primeiros e
 * os acertos do 6º ao 10º prêmio ficavam sem destaque no bilhete.
 *
 * @param {object} aposta
 * @returns {string[]} milhares sorteadas (posição = nº do sorteio)
 */
function numerosSorteados(aposta) {
  // Bloco já resolvido pelo backend, com o mesmo resolver da conferência.
  const premios = aposta.resultado_concurso?.[0]?.premios;

  if (premios?.length) {
    const total = Math.max(...premios.map(premio => premio.posicao));
    const numeros = new Array(total).fill('');

    premios.forEach(premio => {
      numeros[premio.posicao - 1] = normalizaResultado(premio.milhar);
    });

    return numeros;
  }

  const resultado = aposta.loteria?.resultado;
  if (!resultado) return [];

  const sorteios = Number(aposta.loteria?.quantidade_sorteios);

  if (sorteios === 7 && resultado['1_ao_7']?.length) return resultado['1_ao_7'];
  if (sorteios === 10 && resultado['1_ao_10']?.length) return resultado['1_ao_10'];

  return resultado.resultados ?? [];
}

/**
 * Bilhete pago continua com os palpites em 'vencedor' no backend. No bilhete a
 * situação do palpite tem que seguir a cor do bilhete (azul), e não ficar verde.
 */
function classeSituacao(situacaoPalpite, situacaoAposta) {
  return situacaoAposta === 'pago' && situacaoPalpite === 'vencedor'
    ? 'pago'
    : situacaoPalpite;
}

/**
 * Retorna quais números do palpite foram premiados, comparando contra
 * os resultados do sorteio — espelhando as regras do backend.
 *
 * @param {object} palpite
 * @param {string[]} resultados  - array indexado 0 (idx+1 = nº do sorteio)
 * @returns {string[]} números premiados
 */
function numerosPremiados(palpite, resultados) {
  if (palpite.situacao !== 'vencedor' && palpite.situacao !== 'pago') return [];
  if (!resultados?.length) return palpite.numeros; // sem resultado carregado: destaca tudo

  const { sorteio_inicial, sorteio_final, modalidade, numeros } = palpite;

  // Sorteios relevantes para este palpite (idx+1 = sorteio 1-based)
  const draws = resultados
    .map(normalizaResultado)
    .filter((_, idx) => {
      const s = idx + 1;
      return s >= sorteio_inicial && s <= sorteio_final;
    })
    .filter(Boolean);

  if (!draws.length) return numeros;

  // Passe e combinados são combinações multi-draw; se o palpite ganhou, todos os números ganharam
  if (modalidade.includes('passe') || modalidade.includes('combinado')) {
    return numeros;
  }

  const matched = numeros.filter(numero => {
    const n = String(numero);

    // F.M.F: o duque/terno confere separado na frente, no meio e no fundo do milhar
    if (n.includes('x') && /^(duque|terno)-dezena-fmf/.test(modalidade)) {
      const lados = [d => d.slice(-2), d => d.slice(-3, -1), d => d.slice(-4, -2)];
      return lados.some(lado =>
        n.split('x').every(p => draws.some(d => lado(String(d)) === p.padStart(2, '0')))
      );
    }

    // Duque/terno de dezena: formato '12x34' — ambas devem estar nos draws
    if (n.includes('x') && modalidade.includes('dezena')) {
      return n.split('x').every(p => draws.some(d => d.slice(-2) === p.padStart(2, '0')));
    }

    // Duque/terno de grupo: formato '1x9' — ambos os grupos devem estar nos draws
    if (n.includes('x') && modalidade.includes('grupo')) {
      return n.split('x').every(g => draws.some(d => grupoDoResultado(d) === parseInt(g, 10)));
    }

    // Milhar-centena: ganha se bater em milhar OU centena
    if (modalidade === 'milhar-centena' || modalidade === 'milhar-centena-invertida') {
      return draws.some(d =>
        d.slice(-4) === n.slice(-4) ||   // milhar
        d.slice(-3) === n.slice(-3)       // centena
      );
    }

    if (modalidade.includes('milhar')) {
      return draws.some(d => d.slice(-4) === n.slice(-4));
    }
    if (modalidade.includes('centena')) {
      return draws.some(d => d.slice(-3) === n.slice(-3));
    }
    if (modalidade.includes('dezena')) {
      return draws.some(d => d.slice(-2) === n.padStart(2, '0'));
    }
    if (modalidade.includes('grupo')) {
      return draws.some(d => grupoDoResultado(d) === parseInt(n, 10));
    }

    return false;
  });

  return matched;
}

const STYLE_PREMIADO = { color: '#28a745', fontWeight: 'bold' };

/**
 * Possível retorno de um palpite: valor × cotação, a mesma conta do
 * RetornoPossivelService do backend (lá somada para o bilhete inteiro).
 *
 * Quem decide se aparece é o backend: com a opção "retorno possível da pule"
 * desligada na banca, o `retorno_possivel` do bilhete vem null e nenhuma linha
 * é mostrada. Palpite sem cotação gravada também devolve null.
 */
function retornoDoPalpite(palpite, aposta) {
  if (aposta.retorno_possivel == null) return null;

  const retorno = Number(palpite.valor) * Number(palpite.cotacao);

  return retorno > 0 ? Math.round(retorno * 100) / 100 : null;
}

/**
 * Bloco de resultado no rodapé do bilhete: os prêmios da extração daquele
 * bilhete, 1º ao 5º à esquerda e do 6º em diante à direita. O título é só
 * "RESULTADO" e o nome da extração vai na linha de baixo, sem negrito — no
 * título ele quebrava a linha com a data na maioria das loterias.
 *
 * Grupo e bicho já vêm resolvidos do backend (`resultado_concurso`, montado pelo
 * ResultadoConcursoService) — não recalcular aqui. Lista vazia enquanto o
 * resultado não sai; nesse caso o bloco não aparece.
 */
function ResultadoConcurso({ blocos }) {
  if (!blocos?.length) return null;

  const linha = premio => (
    <div key={premio.posicao} className='resultadoConcursoPremio'>
      <span>{premio.posicao}º</span>
      <b>{premio.milhar}</b>
      <span className='resultadoConcursoBicho'>
        {premio.grupo_formatado} {premio.animal}
      </span>
    </div>
  );

  return blocos.map(bloco => {
    const primeiros = bloco.premios.filter(premio => premio.posicao <= 5);
    const demais = bloco.premios.filter(premio => premio.posicao > 5);

    return (
      <div key={`${bloco.loteria_id}_${bloco.data_resultado}`} className='resultadoConcurso'>
        <hr/>
        <div className='resultadoConcursoTitulo'>
          <b>🏆 RESULTADO</b>
          {bloco.data_resultado && (
            <span>{dayjs(bloco.data_resultado).format('DD/MM/YYYY')}</span>
          )}
        </div>
        {bloco.loteria_nome && (
          <div className='resultadoConcursoExtracao'>{bloco.loteria_nome}</div>
        )}
        <div className='resultadoConcursoColunas'>
          <div>{primeiros.map(linha)}</div>
          {demais.length > 0 && <div>{demais.map(linha)}</div>}
        </div>
      </div>
    );
  });
}

const Bilhete = ({aposta, nivel, tipo}) => {
  const sorteados = numerosSorteados(aposta);

  return (
    <>
      <div className='bilheteContainer'>
        <div className='logoDiv'>
          <Logo/>
        </div>

        <h3 style={{fontWeight: 'bold', fontFamily: 'arial'}}>
          Bilhete: {aposta.codigo}
        </h3>
        <span style={{fontFamily: 'arial', color: '#000'}}>
          Emitido: {dayjs(aposta.data_aposta).format('DD/MM/YYYY HH:mm:ss')}
        </span>

        {aposta.data_resultado && (
          <>
            <br/>
            <span style={{fontFamily: 'arial', color: '#000'}}>
              Sorteio: {dayjs(aposta.data_resultado).format('DD/MM/YYYY')}
            </span>
          </>
        )}

        <br/>
        <span>
          Ponto: {nivel === 'admin' ? aposta.operador : aposta.operador.login}
        </span>
        <br/>

        <span>
          Situação:&nbsp;
          <span className={`badge ${aposta.situacao}`}>
            {aposta.situacao.toLowerCase().replace(/(?:^|\s)\S/g, function (a) {
              return a.toUpperCase();
            })}
            
          </span>

          {aposta.situacao === 'pago' || aposta.situacao === 'vencedor' ? (
            <span style={{marginLeft: '10px'}}>{formatMoney(aposta.premio, 2)}</span>
          ) : ''}
        </span>
        <hr/>
        <h4>
          {aposta.loteria.nome}
        </h4>
        <hr/>

        {aposta.palpites.map((palpite, index) => {
          if (palpite.modalidade === 'milhar-brinde') {
            return null;
          }

          const premiados = numerosPremiados(palpite, sorteados);
          const isPremiado = (n) => premiados.includes(n);
          const numerosOrdenados = [...palpite.numeros].sort((a, b) => parseInt(a) - parseInt(b));

          return (
            <div key={index}>
                  <span style={{textTransform: 'uppercase'}}>
                    {palpite.modalidade_nome.replace(/duque\s*-\s*grupo\s*-\s*combinado/gi, "Grupo Combinado")}
                  </span>
              <br/>
              {palpite.modalidade === 'grupo' ? (
                numerosOrdenados.map((numero, indice) => (
                  <div key={indice} style={isPremiado(numero) ? STYLE_PREMIADO : {}}>
                    <b> {calcularGrupo(numero, tipo)} </b>
                    <br/>
                  </div>
                ))
              ) : (
                <>
                  <b>
                    {numerosOrdenados.map((numero, indice) => (
                      <span key={indice} style={isPremiado(numero) ? STYLE_PREMIADO : {}}>
                        {indice > 0 ? ' ' : ''}{numero}
                      </span>
                    ))}
                  </b>
                  <br/>
                </>
              )}
              <span>
                    {palpite.sorteio_inicial}/{palpite.sorteio_final} a&nbsp;
                {formatMoney(palpite.valor_palpite, 2, false)} =&nbsp;
                {formatMoney(palpite.valor, 2, false)}
                  </span>
              <br/>

              {retornoDoPalpite(palpite, aposta) !== null && (
                <>
                  <span>Possível retorno: {formatMoney(retornoDoPalpite(palpite, aposta))}</span>
                  <br/>
                </>
              )}

              {aposta.situacao === 'vencedor' || aposta.situacao === 'pago' ? (
                <span>
                    Situação:&nbsp;
                  <span className={`badge ${classeSituacao(palpite.situacao, aposta.situacao)}`}>
                      {palpite.situacao
                        .toLowerCase()
                        .replace(/(?:^|\s)\S/g, function (a) {
                          return a.toUpperCase();
                        })}
                  </span>

                  <span style={{marginLeft: '10px'}}>{formatMoney(palpite.premio ?? 0, 2)}</span>
                </span>
              ) : ''}
              <hr/>
            </div>
          );
        })}

        {aposta.palpites.map((palpite, index) => {
          if (palpite.modalidade !== 'milhar-brinde') {
            return null;
          }

          return (
            <div key={index}>
              <span>MILHAR BRINDE 1/1 {palpite.numeros.join(' ')}</span>
              <br/>

              {palpite.situacao === 'pago' || palpite.situacao === 'vencedor' ? (

                <span>
                    Situação:&nbsp;
                  <span className={`badge ${classeSituacao(palpite.situacao, aposta.situacao)}`}>
                      {palpite.situacao
                        .toLowerCase()
                        .replace(/(?:^|\s)\S/g, function (a) {
                          return a.toUpperCase();
                        })}
                  </span>
                  
                  <span style={{marginLeft: '10px'}}>{formatMoney(palpite.premio, 2)}</span>
                </span>
              ) : ''}
              <hr/>
            </div>
          );
        })}

        <h4 style={{fontWeight: 'bold'}}>
          TOTAL: {formatMoney(aposta.valor)}
        </h4>

        <SorteioPremioBilhete sorteioPremio={aposta.sorteio_premio}/>
        <br/>
        <span>{aposta.texto_bilhete}</span>

        <ResultadoConcurso blocos={aposta.resultado_concurso}/>
      </div>

      {aposta.pago_em ? (
        <>
          <br/>
          <div className={`boxx ${aposta.situacao}`}>
            <span style={{fontWeight: 'bold'}}>
              Pago por: {aposta?.usuario_pagou?.nivel} <br/>
              {dayjs(aposta.pago_em).format('DD/MM/YYYY HH:mm:ss')}
            </span>
          </div>
        </>
      ) : (
        <>
          <br/>
          {aposta.cancelado_em && (
            <div className={`boxx ${aposta.situacao}`}>
              <span style={{fontWeight: 'bold'}}>
                Cancelado por: {aposta.usuario_cancelou.nivel} <br/>
                {dayjs(aposta.cancelado_em).format('DD/MM/YYYY HH:mm:ss')}
              </span>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Bilhete;
